import React, { useState, useMemo, useEffect, useRef } from 'react'
import PageHeader from '@/components/PageHeader'
import BluePlaceholder from '@/components/BluePlaceholder'
import { CATEGORIES } from '@/data/projects'
import api, { imgSrc } from '@/api/index'

function StatusBadge({ status }) {
  const isOngoing = status === 'ongoing';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999,
      background: isOngoing ? 'rgba(27,123,63,0.12)' : 'rgba(43,43,43,0.06)',
      color: isOngoing ? 'var(--accent-deep)' : 'var(--fg-2)',
      border: '1px solid', borderColor: isOngoing ? 'rgba(27,123,63,0.25)' : 'var(--line-soft)',
      fontFamily: 'var(--text)', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
    }}>
      {isOngoing && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--accent)', animation: 'pulse-dot 2s infinite' }} />}
      {isOngoing ? 'Ongoing' : `Completed${status === 'completed' ? '' : ''}`}
    </span>
  );
}

function ProjectCard({ p, onClick, view }) {
  if (view === 'list') {
    return (
      <button onClick={onClick} className="lift" style={{
        all: 'unset', cursor: 'pointer', display: 'block', width: '100%',
        padding: '24px 0', borderBottom: '1px solid var(--line-soft)',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 200px 200px 24px', gap: 24, alignItems: 'center' }} className="list-row">
          <StatusBadge status={p.status} />
          <div>
            <div className="display" style={{ fontSize: 20, color: 'var(--fg-0)' }}>{p.title}</div>
            <div style={{ fontSize: 13, color: 'var(--fg-2)', marginTop: 4 }}>{p.scope}</div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>{p.client}</div>
          <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>{p.location} · {p.year}</div>
          <div style={{ color: 'var(--accent)', fontSize: 18 }}>→</div>
        </div>
        <style>{`@media (max-width: 980px) { .list-row { grid-template-columns: 1fr !important; gap: 8px !important; } .list-row > :last-child { display: none; } }`}</style>
      </button>
    );
  }
  return (
    <article onClick={onClick} className="panel lift" style={{ padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative' }}>
        <BluePlaceholder label="" sub={p.year} variant={p.variant}
          src={imgSrc(p.images?.[0] || p.img)}
          height={260} />
        <div style={{ position: 'absolute', top: 14, left: 14 }}>
          <StatusBadge status={p.status} />
        </div>
        {(p.images?.length > 1) && (
          <div style={{ position: 'absolute', bottom: 12, right: 12, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 999, padding: '3px 10px', fontFamily: 'var(--mono)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="m3 9 4-4 4 4 4-4 4 4"/><path d="m3 15 4 4 4-4 4 4 4-4"/></svg>
            {p.images.length} photos
          </div>
        )}
      </div>
      <div style={{ padding: 22, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div style={{ fontSize: 12, color: 'var(--fg-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 500 }}>{p.category}</span>
          <span>{p.location}</span>
        </div>
        <h3 className="display" style={{ fontSize: 22, marginTop: 10, color: 'var(--fg-0)', fontWeight: 600 }}>{p.title}</h3>
        <p style={{ marginTop: 10, fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.5, flex: 1 }}>{p.scope}</p>
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line-soft)', fontSize: 12, color: 'var(--fg-2)', display: 'flex', justifyContent: 'space-between' }}>
          <span>For <span style={{ color: 'var(--fg-0)', fontWeight: 500 }}>{p.client}</span></span>
          <span>{p.status === 'ongoing' ? 'In progress' : `Delivered ${p.year}`}</span>
        </div>
      </div>
    </article>
  );
}

function ProjectDetail({ p, onClose }) {
  const [photoIdx, setPhotoIdx] = React.useState(0);

  // Normalise: prefer images[], fall back to img
  // NOTE: useMemo must run before any early return (hooks rule), so guard !p inside
  const photos = React.useMemo(() => {
    if (!p) return [];
    if (p.images?.length) return p.images;
    if (p.img) return [p.img];
    return [];
  }, [p]);

  if (!p) return null;

  const prev = () => setPhotoIdx(i => (i - 1 + photos.length) % photos.length);
  const next = () => setPhotoIdx(i => (i + 1) % photos.length);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(43,43,43,0.55)', backdropFilter: 'blur(8px)',
      overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        maxWidth: 1100, margin: '60px auto', background: 'var(--bg-0)',
        border: '1px solid var(--line)', borderRadius: 18, overflow: 'hidden', position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 20, right: 20, zIndex: 10,
          background: '#fff', border: '1px solid var(--line)', color: 'var(--fg-0)',
          padding: '8px 16px', cursor: 'pointer', fontFamily: 'var(--text)', fontSize: 13, fontWeight: 500,
          borderRadius: 999,
        }}>Close ✕</button>

        {/* ── Photo gallery area ── */}
        <div style={{ position: 'relative' }}>
          {/* Main photo */}
          <BluePlaceholder
            label="" sub="" variant={p.variant}
            src={imgSrc(photos[photoIdx]) || (photos[photoIdx] || '')}
            height={460}
          />

          {/* Status badge */}
          <div style={{ position: 'absolute', top: 20, left: 20, zIndex: 2 }}>
            <StatusBadge status={p.status} />
          </div>

          {/* Prev / Next arrows (only when multiple photos) */}
          {photos.length > 1 && (
            <>
              <button onClick={prev} style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 4,
                width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.92)',
                border: '1px solid var(--line)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              }}>‹</button>
              <button onClick={next} style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', zIndex: 4,
                width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.92)',
                border: '1px solid var(--line)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              }}>›</button>

              {/* Photo counter */}
              <div style={{
                position: 'absolute', bottom: 16, right: 16, zIndex: 4,
                background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 999,
                padding: '4px 12px', fontFamily: 'var(--mono)', fontSize: 12,
              }}>{photoIdx + 1} / {photos.length}</div>
            </>
          )}
        </div>

        {/* ── Thumbnail strip (only when multiple photos) ── */}
        {photos.length > 1 && (
          <div style={{ display: 'flex', gap: 8, padding: '12px 24px', background: 'var(--bg-1)', borderBottom: '1px solid var(--line-soft)', overflowX: 'auto' }}>
            {photos.map((src, i) => (
              <button key={i} onClick={() => setPhotoIdx(i)} style={{
                flexShrink: 0, width: 72, height: 54, borderRadius: 10, overflow: 'hidden',
                border: i === photoIdx ? '2.5px solid var(--accent)' : '2px solid transparent',
                cursor: 'pointer', padding: 0, background: 'var(--bg-2)',
                boxShadow: i === photoIdx ? '0 0 0 2px var(--accent-soft)' : 'none',
                transition: 'border-color .15s',
              }}>
                <img src={imgSrc(src) || src} alt={`Photo ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </button>
            ))}
          </div>
        )}

        {/* ── Text content ── */}
        <div style={{ padding: '40px 48px 48px' }} className="detail-pad">
          <div style={{ fontSize: 13, color: 'var(--fg-2)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>{p.category}</span><span>·</span><span>{p.location}</span><span>·</span><span>{p.year}</span>
          </div>
          <h2 className="display" style={{ fontSize: 'clamp(32px, 4.5vw, 56px)', marginTop: 16, color: 'var(--fg-0)', fontWeight: 600 }}>{p.title}</h2>
          <p style={{ fontSize: 17, color: 'var(--fg-1)', lineHeight: 1.6, marginTop: 24, maxWidth: 720 }}>{p.brief}</p>

          <div style={{ marginTop: 40, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, border: '1px solid var(--line-soft)', borderRadius: 12, overflow: 'hidden' }} className="detail-grid">
            {[
              ['Client', p.client],
              ['Location', p.location],
              ['Scope', p.scope],
              [p.status === 'ongoing' ? 'Status' : 'Completed', p.status === 'ongoing' ? 'In progress' : p.year],
            ].map((r, i) => (
              <div key={i} style={{ padding: '20px 22px', background: '#fff', borderRight: i < 3 ? '1px solid var(--line-soft)' : 'none' }}>
                <div style={{ fontFamily: 'var(--text)', fontSize: 11, color: 'var(--fg-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{r[0]}</div>
                <div style={{ fontSize: 14, color: 'var(--fg-0)', marginTop: 6, fontWeight: 500 }}>{r[1]}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 32, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(p.tags || []).map(t => <span key={t} className="chip">{t}</span>)}
          </div>
        </div>
        <style>{`@media (max-width: 720px) { .detail-pad { padding: 24px !important; } .detail-grid { grid-template-columns: 1fr 1fr !important; } .detail-grid > * { border-right: none !important; border-bottom: 1px solid var(--line-soft); } }`}</style>
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('All');
  const [view, setView] = useState('grid');
  const [active, setActive] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  useEffect(() => {
    api.get('/projects')
      .then(({ data }) => setAllProjects(data.data))
      .finally(() => setLoadingProjects(false));
  }, []);

  const statusCounts = useMemo(() => ({
    all: allProjects.length,
    ongoing: allProjects.filter(p => p.status === 'ongoing').length,
    completed: allProjects.filter(p => p.status === 'completed').length,
  }), [allProjects]);

  const counts = useMemo(() => {
    const base = status === 'all' ? allProjects : allProjects.filter(p => p.status === status);
    const c = { All: base.length };
    CATEGORIES.slice(1).forEach(cat => { c[cat] = base.filter(p => p.category === cat).length; });
    return c;
  }, [status, allProjects]);

  const filtered = useMemo(() => {
    let list = status === 'all' ? allProjects : allProjects.filter(p => p.status === status);
    if (category !== 'All') list = list.filter(p => p.category === category);
    return [...list].sort((a, b) => {
      const ay = parseInt(a.year) || 9999;
      const by = parseInt(b.year) || 9999;
      if (a.status !== b.status) return a.status === 'ongoing' ? -1 : 1;
      return by - ay;
    });
  }, [status, category, allProjects]);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Our work"
        title={<>Built <span style={{ color: 'var(--accent)' }}>for Nepal</span>.</>}
        sub="Every project we&rsquo;ve put our name on — from family homes and schools to hospitals, NGOs and embassy compounds. Filter by status or sector to find what you&rsquo;re looking for."
        badge={`${allProjects.length} projects · ${statusCounts.ongoing} ongoing · ${statusCounts.completed} completed`}
        variant="projects"
      />
      <div style={{ position: 'sticky', top: 64, zIndex: 50, background: 'rgba(246,244,239,0.94)', backdropFilter: 'blur(14px)', borderBottom: '1px solid var(--line-soft)' }}>
        <div className="container filter-bar" style={{ padding: '16px 48px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

          {/* Status tabs */}
          <div style={{ display: 'inline-flex', background: 'var(--bg-1)', borderRadius: 999, padding: 4 }} className="status-tabs">
            {[
              ['all', 'All', statusCounts.all],
              ['ongoing', 'Ongoing', statusCounts.ongoing],
              ['completed', 'Completed', statusCounts.completed],
            ].map(([k, l, n]) => (
              <button key={k} onClick={() => setStatus(k)} style={{
                background: status === k ? 'var(--accent)' : 'transparent',
                color: status === k ? '#fff' : 'var(--fg-1)',
                border: 'none', padding: '8px 16px', cursor: 'pointer',
                fontFamily: 'var(--text)', fontSize: 13, fontWeight: 500,
                borderRadius: 999, transition: 'all .2s',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
                {k === 'ongoing' && status === k && <span style={{ width: 6, height: 6, borderRadius: 999, background: '#fff', animation: 'pulse-dot 2s infinite' }} />}
                {l}
                <span style={{ opacity: 0.7, fontFamily: 'var(--mono)', fontSize: 11 }}>{n}</span>
              </button>
            ))}
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--line)' }} className="filter-sep" />

          {/* Category chips */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)} style={{
                background: c === category ? 'var(--fg-0)' : 'transparent',
                color: c === category ? '#fff' : 'var(--fg-1)',
                border: '1px solid', borderColor: c === category ? 'var(--fg-0)' : 'var(--line)',
                padding: '6px 14px', cursor: 'pointer',
                fontFamily: 'var(--text)', fontSize: 12, fontWeight: 500, letterSpacing: '0',
                borderRadius: 999, transition: 'all .2s',
              }}>
                {c}{counts[c] !== undefined && counts[c] > 0 && <span style={{ opacity: 0.5, marginLeft: 6, fontSize: 11 }}>{counts[c]}</span>}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', border: '1px solid var(--line)', borderRadius: 999, padding: 2 }} className="view-toggle">
            {[['grid', '▦'], ['list', '☰']].map(([v, icon]) => (
              <button key={v} onClick={() => setView(v)} style={{
                background: v === view ? 'var(--fg-0)' : 'transparent',
                color: v === view ? '#fff' : 'var(--fg-1)',
                border: 'none', padding: '6px 12px', cursor: 'pointer',
                fontSize: 14,
                borderRadius: 999,
              }}>{icon}</button>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 720px) {
            .filter-sep { display: none; }
          }
        `}</style>
      </div>

      <section style={{ padding: '48px 0' }}>
        <div className="container">
          {loadingProjects ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="proj-grid">
              {[0,1,2,3,4,5].map(i => (
                <div key={i} className="panel" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ height: 260, background: 'var(--bg-2)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ padding: 22 }}>
                    {[70, 90, 100, 60].map((w, j) => (
                      <div key={j} style={{ height: 12, width: `${w}%`, background: 'var(--bg-2)', borderRadius: 6, marginBottom: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    ))}
                  </div>
                </div>
              ))}
              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }`}</style>
            </div>
          ) : filtered.length === 0 && allProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--fg-2)' }}>
              <div className="mono" style={{ fontSize: 13, color: 'var(--fg-3)' }}>No projects yet</div>
              <p style={{ marginTop: 8, fontSize: 14 }}>Add projects from the admin panel to display them here.</p>
            </div>
          ) : view === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }} className="proj-grid">
              {filtered.map(p => <ProjectCard key={p._id} p={p} onClick={() => setActive(p)} view={view} />)}
            </div>
          ) : (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 200px 200px 24px', gap: 24, padding: '12px 0', borderBottom: '1px solid var(--line)' }} className="list-row">
                <span style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Status</span>
                <span style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Project</span>
                <span style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Client</span>
                <span style={{ fontSize: 11, color: 'var(--fg-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Location · Year</span>
                <span></span>
              </div>
              {filtered.map(p => <ProjectCard key={p._id} p={p} onClick={() => setActive(p)} view={view} />)}
            </div>
          )}
          {!loadingProjects && allProjects.length > 0 && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 80, color: 'var(--fg-2)' }}>
              <div style={{ fontSize: 15 }}>No projects match this filter.</div>
            </div>
          )}
        </div>
        <style>{`@media (max-width: 1100px) { .proj-grid { grid-template-columns: 1fr 1fr !important; } } @media (max-width: 720px) { .proj-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      <ProjectDetail p={active} onClose={() => setActive(null)} />
    </div>
  );
}
